/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-enum-comparison, react-hooks/exhaustive-deps, @typescript-eslint/strict-boolean-expressions */

import {
  Button,
  FormControlLabel,
  Typography,
  Stack,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  ClickAwayListener,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { updateSearch, getDefaultSlotProps, dayjs } from '../../util';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { ehModelTypeState, synthOISelectedDateState } from '../../states';
import { EhModelType } from '../../types';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowDropDownIcon,
  DatePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import useIsMobile from 'hooks/useIsMobile';
import useSynthOi from 'hooks/equityhub/useSynthOi';

export const SynthOIHeaderWidgetPreamble = ({
  setUpsellOpen,
}: {
  setUpsellOpen: (upsellOpen: boolean) => void;
}) => {
  const theme = useTheme();

  const isMobile = useIsMobile();
  const setEhModelType = useSetRecoilState(ehModelTypeState);
  const [searchParams, setSearchParams] = useSearchParams();

  const { isSynthOI, hasSynthOiAccess } = useSynthOi();

  const setSynthOiDate = useSetRecoilState(synthOISelectedDateState);

  useEffect(() => {
    const date = searchParams.get('date');
    if (date) {
      setSynthOiDate(dayjs(date));
    } else {
      setSearchParams(updateSearch({ date: dayjs().format('YYYY-MM-DD') }));
    }
  }, [searchParams]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleModelChange = (value: EhModelType) => {
    setSearchParams(updateSearch({ 'eh-model': value }));
    setEhModelType(value);
    handleClose();
  };

  return (
    <>
      <Button
        onClick={handleClick}
        variant="outlined"
        color="primary"
        endIcon={<ArrowDropDownIcon />}
        sx={{
          borderRadius: '4px',
          minWidth: '100px',
          fontSize: isMobile ? 11 : 13,
          fontWeight: 500,
          textTransform: 'none',
          px: 1.5,
          py: 0.5,
        }}
      >
        {isSynthOI ? 'Synthetic OI Model' : 'Total OI Model'}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{ zIndex: 2 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            sx={{
              mt: 1,
              p: 4,
              boxShadow: theme.palette.shadows.default,
              borderRadius: '4px',
              width: { xs: 260, md: 350 },
            }}
          >
            <RadioGroup
              value={isSynthOI ? EhModelType.SynthOI : EhModelType.Legacy}
              defaultValue={EhModelType.SynthOI}
              onChange={(_e, value) => {
                if (value === EhModelType.SynthOI && !hasSynthOiAccess) {
                  setUpsellOpen(true);
                  handleClose();
                } else {
                  handleModelChange(value as EhModelType);
                }
              }}
              sx={{ gap: '12px' }}
            >
              <FormControlLabel
                value={EhModelType.SynthOI}
                control={<Radio />}
                label={
                  <Stack>
                    <Typography
                      sx={{
                        fontSize: { xs: 12, md: 14 },
                        color: isSynthOI ? 'primary.main' : 'text.secondary',
                      }}
                    >
                      Synthetic OI Model
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      This model delivers more precise tracking of short-term
                      options hedging flows by eliminating assumptions and
                      categorizing transactions based on multiple new data
                      feeds.
                    </Typography>
                  </Stack>
                }
              />
              <FormControlLabel
                value={EhModelType.Legacy}
                control={<Radio />}
                label={
                  <Stack>
                    <Typography
                      sx={{
                        fontSize: { xs: 12, md: 14 },
                        color: isSynthOI ? 'text.secondary' : 'primary.main',
                      }}
                    >
                      Total OI Model
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      SpotGamma's legacy Equity Hub model with years of validity
                      and data supporting its impact. This model assumes that
                      all options are sold by market makers.
                    </Typography>
                  </Stack>
                }
              />
            </RadioGroup>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export const SynthOIHeaderWidgetPostAmble = () => {
  const theme = useTheme();
  const { isSynthOI } = useSynthOi();

  const [synthOiDate, setSynthOiDate] = useRecoilState(
    synthOISelectedDateState,
  );

  const [_, setSearchParams] = useSearchParams();

  const defaultSlotProps = getDefaultSlotProps(theme);

  const handleDateChange = (date: dayjs.Dayjs | null) => {
    // Only process valid dates to reduce backend requests
    if (date && date.isValid()) {
      // Check if it's not a weekend
      if (date.day() !== 0 && date.day() !== 6) {
        // Check if it's within the allowed date range
        const minDate = dayjs('2024-09-03');
        const maxDate = dayjs();
        if (
          date.isAfter(minDate.subtract(1, 'day')) &&
          date.isBefore(maxDate.add(1, 'day'))
        ) {
          const resultingDate = date;
          setSynthOiDate(resultingDate);
          setSearchParams(
            updateSearch({ date: resultingDate.format('YYYY-MM-DD') }),
          );
        }
      }
    } else {
      // Handle null or invalid date by using current date
      const resultingDate = dayjs();
      setSynthOiDate(resultingDate);
      setSearchParams(
        updateSearch({ date: resultingDate.format('YYYY-MM-DD') }),
      );
    }
  };

  if (!isSynthOI) {
    return null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label="Date"
        value={synthOiDate}
        onChange={handleDateChange}
        minDate={dayjs('2024-09-03')}
        maxDate={dayjs()}
        disableFuture
        shouldDisableDate={(date) => {
          return date.day() === 0 || date.day() === 6; // 0 is Sunday, 6 is Saturday
        }}
        slotProps={{
          textField: {
            size: 'small',
            ...defaultSlotProps.textField,
            style: {
              fontSize: '14px',
            },
          },
          ...defaultSlotProps,
        }}
        format="YYYY-MM-DD"
      />
    </LocalizationProvider>
  );
};
